export interface Doc {
    id: string;
    title: string;
    description: string;
    use: string;
    resources: string[] | { title: string, link: string}[];
    screenshot?: string;
  }

  const docs: Doc[] = [
    {
      id: "crawlMap",
      title: "Crawl Map",
      description:
        "The Crawl Map visualizes the network of connections and relationships between different URLs and domains discovered during a web crawl.",
      use: "This visualization helps in understanding the structure and interconnections of a website, identifying potential security risks, and discovering hidden or related content.",
      resources: [
        { title: "Web Crawling - Wiki", link: "https://en.wikipedia.org/wiki/Web_crawler" },
        { title: "Introduction to Web Crawling", link: "https://www.geeksforgeeks.org/introduction-to-web-crawling/" },
        { title: "Web Scraping and Crawling for Beginners", link: "https://realpython.com/web-scraping-and-crawling-for-beginners/" },
        { title: "Web Crawler - MDN", link: "https://developer.mozilla.org/en-US/docs/Web/Crawler" },
      ],
      // screenshot: "https://i.ibb.co/7XntBfp/redirects.png", // Replace with an appropriate screenshot URL
    },
    {
      id: "crawlUrls",
      title: "Crawl URLs",
      description:
        "The Crawl URLs component lists all the URLs discovered during a web crawl, along with their associated metadata such as country information and domain names.",
      use: "This list helps in identifying all the pages and resources linked to a website, understanding its structure, and discovering hidden or related content.",
      resources: [
        { title: "Web Crawling - Wiki", link: "https://en.wikipedia.org/wiki/Web_crawler" },
        { title: "Introduction to Web Crawling", link: "https://www.geeksforgeeks.org/introduction-to-web-crawling/" },
        { title: "Web Scraping and Crawling for Beginners", link: "https://realpython.com/web-scraping-and-crawling-for-beginners/" },
        { title: "Web Crawler - MDN", link: "https://developer.mozilla.org/en-US/docs/Web/Crawler" },
      ],
      // screenshot: "https://i.ibb.co/7XntBfp/redirects.png", // Replace with an appropriate screenshot URL
    },
    {
      id: "get-ip",
      title: "IP Information",
      description:
        "An IP address is a unique identifier assigned to each device connected to the internet. The IP of a domain can be found by querying the Domain Name System (DNS) for the domain's A (address) record.",
      use: "Finding a server's IP is the first step in further analysis, allowing insights into its network structure, physical location, hosting provider, and other domains on the same IP.",
      resources: [
        { title: 'Understanding IP Addresses', link: 'https://www.digitalocean.com/community/tutorials/understanding-ip-addresses-subnets-and-cidr-notation-for-networking'},
        { title: 'IP Addresses - Wiki', link: 'https://en.wikipedia.org/wiki/IP_address'},
        { title: 'RFC-791 Internet Protocol', link: 'https://tools.ietf.org/html/rfc791'},
        { title: 'What is My IP', link: 'https://whatismyipaddress.com/'},
      ],
    },
    {
      id: "ssl",
      title: "SSL Certificates",
      description:
        "SSL certificates are digital certificates that authenticate a website’s identity and enable secure HTTPS communication. These certificates are issued by trusted Certificate Authorities (CAs).",
      use: "SSL certificates provide encrypted communication and trust indicators for users, while also revealing OSINT data like the issuing authority, domain name, and expiration date, which can assist in verifying the website and finding associated subdomains.",
      resources: [
        { title: 'TLS - Wiki', link: 'https://en.wikipedia.org/wiki/Transport_Layer_Security'},
        { title: 'What is SSL (Cloudflare)', link: 'https://www.cloudflare.com/learning/ssl/what-is-ssl/'},
        { title: 'RFC-8446 - TLS', link: 'https://tools.ietf.org/html/rfc8446'},
        { title: 'SSL Checker', link: 'https://www.sslshopper.com/ssl-checker.html'},
      ],
      screenshot: 'https://i.ibb.co/1mxsKpT/ssl-certificate.png',
    },
    {
      id: "dns",
      title: "DNS Records",
      description:
        "DNS records translate human-readable domain names into IP addresses. Common record types include A (address), MX (mail), NS (name server), CNAME (canonical name), and TXT (text).",
      use: "Examining DNS records can provide valuable information such as IP addresses, email providers, and other infrastructure details, helping to understand the setup and management of a domain.",
      resources: [
        { title: 'DNS Records (Cloudflare)', link: 'https://www.cloudflare.com/learning/dns/dns-records/'},
        { title: 'DNS Record Types', link: 'https://en.wikipedia.org/wiki/List_of_DNS_record_types'},
        { title: 'RFC-1035 - DNS', link: 'https://tools.ietf.org/html/rfc1035'},
        { title: 'DNS Lookup (MxToolbox)', link: 'https://mxtoolbox.com/DNSLookup.aspx'},
      ],
      screenshot: 'https://i.ibb.co/fNrhQBS/DNSrecords.png',
    },
    {
      id: "cookies",
      title: "Cookies",
      description:
        "Cookies are small data files stored on the user's device by websites, often containing session information, preferences, or tracking data.",
      use: "Cookies can reveal information about user tracking, session management, and site security settings, helping to understand privacy practices and user interaction policies.",
      resources: [
        { title: 'HTTP Cookies (Mozilla)', link: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Cookies' },
        { title: 'What are Cookies (Cloudflare)', link: 'https://www.cloudflare.com/learning/privacy/what-are-cookies/' },
        { title: 'Testing Cookie Attributes (OWASP)', link: 'https://owasp.org/www-project-web-security-testing-guide/v42/4-Web_Application_Security_Testing/06-Session_Management_Testing/02-Testing_for_Cookies_Attributes' },
        { title: 'RFC-6265 - Cookies', link: 'https://tools.ietf.org/html/rfc6265' },
      ],
      screenshot: 'https://i.ibb.co/0tYTMwL/cookies.png',
    },
    {
      id: "robots-txt",
      title: "Crawl Rules",
      description:
        "The robots.txt file, usually located at the domain's root, tells search engines which pages to ignore. This helps control bot traffic on websites but isn’t used for hiding pages from search results.",
      use: "Checking robots.txt can sometimes reveal unlisted directories and provide insight into a site's indexing strategies and security practices.",
      resources: [
        { title: 'Google Search Docs - Robots.txt', link: 'https://developers.google.com/search/docs/advanced/robots/intro' },
        { title: 'Robots.txt (Moz)', link: 'https://moz.com/learn/seo/robotstxt' },
        { title: 'RFC-9309 - Robots Exclusion Protocol', link: 'https://datatracker.ietf.org/doc/rfc9309/' },
        { title: 'Robots.txt - wiki', link: 'https://en.wikipedia.org/wiki/Robots_exclusion_standard' },
      ],
      screenshot: 'https://i.ibb.co/f9c7V3g/crawlrule.png',
    },
    {
      id: "headers",
      title: "HTTP Headers",
      description:
        "HTTP headers contain key-value pairs that are exchanged during HTTP requests and responses, including cache policies, security settings, content type, and server details.",
      use: "Analyzing headers can reveal server configurations, security measures, and performance optimizations, offering insights into a site's technology stack and vulnerabilities.",
      resources: [
        { title: 'HTTP Headers (Mozilla)', link: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers' },
        { title: 'RFC-7231 - Headers', link: 'https://datatracker.ietf.org/doc/html/rfc7231#section-7' },
        { title: 'List of HTTP Headers', link: 'https://en.wikipedia.org/wiki/List_of_HTTP_header_fields' },
        { title: 'OWASP Secure Headers', link: 'https://owasp.org/www-project-secure-headers/' },
      ],
      screenshot: 'https://i.ibb.co/J77mS3X/headers.png',
    },
    {
      id: "quality",
      title: "Quality Metrics",
      description:
        "Using Lighthouse, this task measures a website's performance, accessibility, best practices, and SEO, returning a simple score and checklist for each category.",
      use: "Useful for assessing a site's technical health, identifying SEO issues, improving accessibility, and maintaining compliance with best practices.",
      resources: [
        { title: 'Lighthouse Docs', link: 'https://developer.chrome.com/docs/lighthouse/' },
        { title: 'Google Page Speed', link: 'https://developers.google.com/speed' },
        { title: 'W3 Accessibility', link: 'https://www.w3.org/WAI/test-evaluate/' },
        { title: 'Google Search Console', link: 'https://search.google.com/search-console' },
        { title: 'SEO Checker', link: 'https://www.seobility.net/en/seocheck/' },
        { title: 'PWA Builder', link: 'https://www.pwabuilder.com/' },
      ],
      screenshot: 'https://i.ibb.co/6B1SNMC/qualitysummary.png',
    },
    {
      id: "location",
      title: "Server Info",
      description:
        "Determines the physical location of the server hosting a website using its IP address and a location database, providing details like address, time zone, and currency.",
      use: "Understanding a server's location helps with optimizing content delivery, compliance with data residency laws, and identifying possible cybersecurity risks in specific regions.",
      resources: [
        { title: 'IP Locator', link: 'https://geobytes.com/iplocator/' },
        { title: 'Internet Geolocation - Wiki', link: 'https://en.wikipedia.org/wiki/Internet_geolocation' },
      ],
      screenshot: 'https://i.ibb.co/BrJfy9L/serverinfo.png',
    },
    {
      id: "userLocation",
      title: "IP Info",
      description:
        "Determines the physical location of the user's device based on IP, using a location database to provide context like address, time zone, and currency.",
      use: "Understanding the user's location helps in personalizing the user experience, enhancing security, and improving analytics insights.",
      resources: [
        { title: 'IP Locator', link: 'https://geobytes.com/iplocator/' },
        { title: 'Internet Geolocation - Wiki', link: 'https://en.wikipedia.org/wiki/Internet_geolocation' },
      ],
      screenshot: 'https://i.ibb.co/6trGsxc/userinfo.png',
    },
    {
        id: "hosts",
        title: "Associated Hosts",
        description:
          "Identify all domains and subdomains linked to the main website domain. This often involves DNS enumeration to find related hostnames and reviewing known DNS records.",
        use: "Understanding a target's full web presence can reveal related projects, test sites, backup domains, or services tied to the main website. A detailed list of associated domains helps map the organization's digital footprint and identify potential vulnerabilities.",
        resources: [
          { title: 'DNS Enumeration - Wiki', link: 'https://en.wikipedia.org/wiki/DNS_enumeration' },
          { title: 'OWASP - Enumerate Applications on Webserver', link: 'https://owasp.org/www-project-web-security-testing-guide/latest/4-Web_Application_Security_Testing/01-Information_Gathering/04-Enumerate_Applications_on_Webserver' },
          { title: 'DNS Enumeration - DNS Dumpster', link: 'https://dnsdumpster.com/' },
          { title: 'Subdomain Finder', link: 'https://subdomainfinder.c99.nl/' },
        ],
        screenshot: 'https://i.ibb.co/25j1sT7/wc-hosts.png',
      },
      {
        id: "redirects",
        title: "Redirect Chain",
        description:
          "Analyze the sequence of HTTP redirects from the initial to the final destination URL. Redirects happen for reasons like enforcing HTTPS, URL shorteners, or new site locations.",
        use: "Mapping redirect chains helps in understanding site structure, potential security issues (like unencrypted redirects), and their impact on performance and SEO. For OSINT, redirect chains reveal relationships between domains and the use of specific technologies.",
        resources: [
          { title: 'HTTP Redirects - MDN', link: 'https://developer.mozilla.org/en-US/docs/Web/HTTP/Redirections' },
          { title: 'URL Redirection - Wiki', link: 'https://en.wikipedia.org/wiki/URL_redirection' },
          { title: '301 Redirects explained', link: 'https://ahrefs.com/blog/301-redirects/' },
        ],
        screenshot: 'https://i.ibb.co/7XntBfp/redirects.png',
      },
      {
        id: "txt-records",
        title: "TXT Records",
        description:
          "TXT records are DNS records that provide text data for external use, such as verifying domain ownership or email security settings.",
        use: "TXT records can reveal details about a domain's email settings, use of services like Google Workspace or Microsoft 365, and security practices like SPF and DKIM. This information gives insight into the organization’s tech stack and potential security posture.",
        resources: [
          { title: 'TXT Records (via Cloudflare Learning)', link: 'https://www.cloudflare.com/learning/dns/dns-records/dns-txt-record/' },
          { title: 'TXT Records - Wiki', link: 'https://en.wikipedia.org/wiki/TXT_record' },
          { title: 'RFC-1464 - TXT Records', link: 'https://datatracker.ietf.org/doc/html/rfc1464' },
          { title: 'TXT Record Lookup (via MxToolbox)', link: 'https://mxtoolbox.com/TXTLookup.aspx' },
        ],
        screenshot: 'https://i.ibb.co/HDVJjYL/txtrecords.png',
      },
      {
        id: "status",
        title: "Server Status",
        description: "Checks if a server is online and responsive.",
        use: "",
        resources: [],
        screenshot: 'https://i.ibb.co/cJHDZFg/serverstatus.png',
      },
      {
        id: "ports",
        title: "Open Ports",
        description:
          "Lists open ports on a server that are ready to receive connections. Each port relates to a service, like HTTP (port 80) or HTTPS (port 443).",
        use: "Knowing open ports helps identify services running on a server, useful for understanding both system vulnerabilities and the nature of its active services.",
        resources: [
          { title: 'List of TCP & UDP Port Numbers', link: 'https://en.wikipedia.org/wiki/List_of_TCP_and_UDP_port_numbers' },
          { title: 'NMAP - Port Scanning Basics', link: 'https://nmap.org/book/man-port-scanning-basics.html' },
        ],
        screenshot: 'https://i.ibb.co/BwnLRg2/openports.png',
      },
      {
        id: "trace-route",
        title: "Traceroute",
        description:
          "Traceroute is a network tool that shows the path a data packet takes from one system to another, recording each stop along the way.",
        use: "For OSINT, traceroute offers insight into network paths, potential routing issues, and the geographic layout of the network supporting a website or service. It can also reveal additional IPs for further investigation.",
        resources: [
          "https://www.cloudflare.com/learning/network-layer/what-is-traceroute/",
          "https://tools.ietf.org/html/rfc1393",
          "https://en.wikipedia.org/wiki/Traceroute",
          "https://www.ripe.net/publications/docs/ripe-611",
        ],
        screenshot: 'https://i.ibb.co/TTjQGPt/traceroute.png',
      },
      {
        id: "carbon",
        title: "Carbon Footprint",
        description:
          "This task estimates a website's carbon footprint based on data transferred, processing demands, and the energy use of its servers.",
        use: "Understanding a website's environmental impact is important for researchers, activists, and those interested in sustainable tech. It adds context to analyses where environmental considerations are relevant.",
        resources: [
          { title: 'WebsiteCarbon - Carbon Calculator', link: 'https://www.websitecarbon.com/' },
          { title: 'The Green Web Foundation', link: 'https://www.thegreenwebfoundation.org/' },
          { title: 'The Eco Friendly Web Alliance', link: 'https://ecofriendlyweb.org/' },
          { title: 'Reset.org', link: 'https://en.reset.org/' },
          { title: 'Your website is killing the planet - via Wired', link: 'https://www.wired.co.uk/article/internet-carbon-footprint' },
        ],
        screenshot: 'https://i.ibb.co/ngqtmTV/carbonfootprint.png',
      },
      {
        id: "server-info",
        title: "Server Info",
        description:
          "This task gathers details about the server hosting a website, such as server type (e.g., Apache, Nginx), hosting provider, and Autonomous System Number (ASN).",
        use: "In OSINT, server information provides clues about a website's hosting region, server technologies, and potential ties to other domains hosted by the same organization.",
        resources: [
          "https://en.wikipedia.org/wiki/List_of_HTTP_header_fields",
          "https://en.wikipedia.org/wiki/Autonomous_system_(Internet)",
          "https://tools.ietf.org/html/rfc7231#section-7.4.2",
          "https://builtwith.com/",
        ],
        screenshot: 'https://i.ibb.co/Mk1jx32/wc-server.png',
      },
      {
        "id": "domain",
        "title": "Domain Whois Lookup",
        "description": "Retrieves Whois records for the domain, providing registrant details, contact info, nameservers, and dates of domain creation and expiration. This info is sourced from Whois database queries.",
        "use": "Whois records reveal key information about the website’s owner, including registration and expiration dates which give insight into its operational history. Although contact details may be masked, nameservers can help identify linked domains and related services.",
        "resources": [
          "https://en.wikipedia.org/wiki/WHOIS",
          "https://www.icann.org/resources/pages/whois-2018-01-17-en",
          "https://whois.domaintools.com/"
        ],
        screenshot: "https://i.ibb.co/m51n9tR/whois.png"
      },
      {
        "id": "whois",
        "title": "Domain Information",
        "description": "Performs a Whois lookup for a domain, providing ownership, registration dates, contact details, and server data, sourced from a Whois database query.",
        "use": "Whois information provides valuable details about a domain's ownership, registration, and related domains. Nameservers and contact info (when visible) can lead to additional investigation avenues.",
        "resources": [
          "https://en.wikipedia.org/wiki/WHOIS",
          "https://www.icann.org/resources/pages/whois-2018-01-17-en",
          "https://whois.domaintools.com/"
        ],
        screenshot: "https://i.ibb.co/m51n9tR/whois.png"
      },
      {
        "id": "dnssec",
        "title": "DNS Security Extensions",
        "description": "DNSSEC secures DNS queries by using digital signatures, protecting against DNS spoofing. Also covered are alternatives like DoH and DoT that further secure the DNS lookup process.",
        "use": "DNSSEC implementation indicates an organization’s cybersecurity posture, helping prevent DNS spoofing or cache poisoning attacks. If not enabled, it may reveal security gaps.",
        "resources": [
          "https://dnssec-analyzer.verisignlabs.com/",
          "https://www.cloudflare.com/dns/dnssec/how-dnssec-works/",
          "https://en.wikipedia.org/wiki/Domain_Name_System_Security_Extensions",
          "https://www.icann.org/resources/pages/dnssec-what-is-it-why-important-2019-03-05-en",
          "https://support.google.com/domains/answer/6147083",
          "https://www.internetsociety.org/resources/deploy360/2013/dnssec-test-sites/"
        ],
        screenshot: "https://i.ibb.co/tpH6KKX/DNSSec.png"
      },
      {
        "id": "features",
        "title": "Site Feature Detection",
        "description": "Determines which core features are actively used on a site. 'Dead' features are identified as those inactive or unused during initial load.",
        "use": "Provides insight into the site’s capabilities and technology use, useful for identifying potential vulnerabilities or assessing the depth of functionality.",
        "resources": [],
        screenshot: "https://i.ibb.co/tzmJtG8/sitefeatures.png"
      },
      {
        "id": "hsts",
        "title": "HTTP Strict Transport Security",
        "description": "HSTS is a security mechanism that forces HTTPS usage, blocking protocol downgrades and preventing cookie hijacking. HSTS-enabled sites redirect HTTP requests to HTTPS automatically, and don’t allow overriding invalid certificate warnings.",
        "use": "HSTS protects against a range of attacks by enforcing secure connections. Understanding if HSTS is enabled provides insight into a site's commitment to user security and helps identify potential vulnerabilities.",
        "resources": [
          "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Strict-Transport-Security",
          "https://cheatsheetseries.owasp.org/cheatsheets/HTTP_Strict_Transport_Security_Cheat_Sheet.html",
          "https://hstspreload.org/"
        ],
        screenshot: "https://i.ibb.co/HhGytjX/hsts.png"
      },
      {
        "id": "dns-server",
        "title": "DNS Server Information",
        "description": "Checks DNS servers for a domain, noting whether they support DNS over HTTPS (DoH) and if they are vulnerable to cache poisoning.",
        "use": "DNS server information helps gauge the site's DNS configuration security and can highlight vulnerabilities, especially around DNS poisoning and spoofing.",
        "resources": [],
        screenshot: "https://i.ibb.co/Mk0P2xD/DNSserver.png"
      },
      {
        "id": "tech-stack",
        "title": "Technology Stack Detection",
        "description": "Identifies technologies used on a website by parsing its HTML and matching against known fingerprints from sources like Wappalyzer.",
        "use": "Knowing a site’s tech stack can reveal potential security risks, support competitive analysis, and inform targeted marketing strategies. Responsible handling of this data is crucial to maintaining privacy and security.",
        "resources": [
          { "title": "Wappalyzer fingerprints", "link": "https://github.com/wappalyzer/wappalyzer/tree/master/src/technologies" },
          { "title": "BuiltWith - Check what tech a site is using", "link": "https://builtwith.com/" }
        ],
        screenshot: "https://i.ibb.co/rxSnJbS/techstack.png"
      },
      {
        "id": "sitemap",
        "title": "Site Sitemap",
        "description": "Finds and analyzes the site's sitemap, listing publicly available pages that the site owner wants search engines to index. Useful for SEO purposes and provides an organized view of the site's public content.",
        "use": "Helps to understand a site's public content layout and structure. For site owners, ensures the sitemap is accessible, properly formatted, and includes desired pages.",
        "resources": [
          { "title": "Learn about Sitemaps", "link": "https://developers.google.com/search/docs/crawling-indexing/sitemaps/overview" },
          { "title": "Sitemap XML spec", "link": "https://www.sitemaps.org/protocol.html" },
          { "title": "Sitemap tutorial", "link": "https://www.conductor.com/academy/xml-sitemap/" }
        ],
        screenshot: "https://i.ibb.co/vdg86Dt/pages.png"
      },
      {
        "id": "security-txt",
        "title": "Security Contact Info (security.txt)",
        "description": "The security.txt file provides a designated contact for reporting security issues found on the site. It adheres to RFC 9116 guidelines and may include details like a contact email, security policy link, and PGP key. The file should be available at /security.txt or /.well-known/security.txt.",
        "use": "Critical for responsible disclosure of security issues, enabling researchers to report them securely. Offers insights into a site’s security practices and, from an OSINT perspective, shows the organization’s approach to cybersecurity and disclosure.",
        "resources": [
          { "title": "securitytxt.org", "link": "https://securitytxt.org/" },
          { "title": "RFC-9116 Proposal", "link": "https://datatracker.ietf.org/doc/html/rfc9116" },
          { "title": "RFC-9116 History", "link": "https://datatracker.ietf.org/doc/rfc9116/" },
          { "title": "Security.txt (Wikipedia)", "link": "https://en.wikipedia.org/wiki/Security.txt" },
          { "title": "Example security.txt (Cloudflare)", "link": "https://www.cloudflare.com/.well-known/security.txt" },
          { "title": "Tutorial for creating security.txt (Pieter Bakker)", "link": "https://pieterbakker.com/implementing-security-txt/" }
        ],
        screenshot: "https://i.ibb.co/R25hvGS/securitytxt.png"
      },
      {
        "id": "linked-pages",
        "title": "Page Links",
        "description": "Extracts and displays all internal and external links on a website, sourced from anchor (href) elements.",
        "use": "For site owners, aids in optimizing SEO, improving site structure, and monitoring link quality. For OSINT researchers, internal links reveal site organization, while external links highlight partnerships, dependencies, and any potential security risks.",
        "resources": [
          { "title": "W3C Link Checker", "link": "https://validator.w3.org/checklink" }
        ],
        screenshot: "https://i.ibb.co/6yBGXHM/linkedpages.png"
      },
      {
        "id": "social-tags",
        "title": "Social Media Tags",
        "description": "Analyzes meta tags that guide social media and search engines on displaying website information, such as titles, descriptions, images, and author information.",
        "use": "Enhances SEO and ensures accurate representation on social media platforms. From an OSINT angle, these tags reveal how a site chooses to publicly portray itself.",
        "resources": [
          { "title": "SocialSharePreview.com", "link": "https://socialsharepreview.com/" },
          { "title": "The guide to social meta tags", "link": "https://css-tricks.com/essential-meta-tags-social-media/" },
          { "title": "Web.dev metadata tags", "link": "https://web.dev/learn/html/metadata/" },
          { "title": "Open Graph Protocol", "link": "https://ogp.me/" },
          { "title": "Twitter Cards", "link": "https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/abouts-cards" },
          { "title": "Facebook Open Graph", "link": "https://developers.facebook.com/docs/sharing/webmasters" }
        ],
        screenshot: "https://i.ibb.co/kM728Sf/socialtags.png"
      },
      {
        "id": "mail-config",
        "title": "Email Security Settings",
        "description": "Evaluates email security configurations such as DMARC, BIMI, DKIM, and SPF, which help verify sender identity, secure email content, and display logos for brand recognition.",
        "use": "Crucial for email authentication and phishing prevention. Provides insights into a domain's email security setup and potential vulnerabilities, useful for OSINT investigators and cybersecurity analysts.",
        "resources": [
          { "title": "Intro to DMARC, DKIM, and SPF (via Cloudflare)", "link": "https://www.cloudflare.com/learning/email-security/dmarc-dkim-spf/" },
          { "title": "EasyDMARC Domain Scanner", "link": "https://easydmarc.com/tools/domain-scanner" },
          { "title": "MX Toolbox", "link": "https://mxtoolbox.com/" },
          { "title": "RFC-7208 - SPF", "link": "https://datatracker.ietf.org/doc/html/rfc7208" },
          { "title": "RFC-6376 - DKIM", "link": "https://datatracker.ietf.org/doc/html/rfc6376" },
          { "title": "RFC-7489 - DMARC", "link": "https://datatracker.ietf.org/doc/html/rfc7489" },
          { "title": "BIMI Group", "link": "https://bimigroup.org/" }
        ],
        screenshot: "https://i.ibb.co/mTqXVXs/emailconfiguration.png"
      },
      {
        "id": "firewall",
        "title": "Web Application Firewall Detection",
        "description": "Identifies the presence of a web application firewall (WAF) and gathers details about its configuration to understand the level of protection against attacks such as XSS and SQL injection.",
        "use": "Useful for assessing a site's defense against various attack types. Knowledge of the WAF can reveal both strengths in security and potential weaknesses.",
        "resources": [
          { "title": "What is a WAF (via Cloudflare Learning)", "link": "https://www.cloudflare.com/learning/ddos/glossary/web-application-firewall-waf/" },
          { "title": "OWASP - Web Application Firewalls", "link": "https://owasp.org/www-community/Web_Application_Firewall" },
          { "title": "Web Application Firewall Best Practices", "link": "https://owasp.org/www-pdf-archive/Best_Practices_Guide_WAF_v104.en.pdf" },
          { "title": "WAF - Wiki", "link": "https://en.wikipedia.org/wiki/Web_application_firewall" }
        ],
        screenshot: "https://i.ibb.co/tM9bPVK/firewall.png"
      },
      {
        "id": "http-security",
        "title": "HTTP Security Headers",
        "description": "Analyzes HTTP security headers, such as HSTS, CSP, X-Content-Type-Options, and X-Frame-Options, which help protect against common web attacks.",
        "use": "Evaluating HTTP headers reveals a site’s security posture and highlights any potential weaknesses, ensuring adherence to security best practices.",
        "resources": [
          { "title": "OWASP Secure Headers Project", "link": "https://owasp.org/www-project-secure-headers/" },
          { "title": "HTTP Header Cheatsheet", "link": "https://cheatsheetseries.owasp.org/cheatsheets/HTTP_Headers_Cheat_Sheet.html" },
          { "title": "content-security-policy.com", "link": "https://content-security-policy.com/" },
          { "title": "resourcepolicy.fyi", "link": "https://resourcepolicy.fyi/" },
          { "title": "HTTP Security Headers", "link": "https://securityheaders.com/" },
          { "title": "Mozilla Observatory", "link": "https://observatory.mozilla.org/" },
          { "title": "CSP Docs", "link": "https://developer.mozilla.org/en-US/docs/Web/HTTP/CSP" },
          { "title": "HSTS Docs", "link": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Strict-Transport-Security" },
          { "title": "X-Content-Type-Options Docs", "link": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/X-Content-Type-Options" },
          { "title": "X-Frame-Options Docs", "link": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/X-Frame-Options" },
          { "title": "X-XSS-Protection Docs", "link": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/X-XSS-Protection" }
        ],
        screenshot: "https://i.ibb.co/8sXtyL8/httpsecurity.png"
      },
      {
        "id": "archives",
        "title": "Site Archive History",
        "description": "Retrieves a site’s full archive history from the Wayback Machine, enabling a view into its changes over time and any previously removed content.",
        "use": "Useful for tracking the evolution of a site, finding removed content, or restoring lost information.",
        "resources": [
          { "title": "Wayback Machine", "link": "https://archive.org/web/" }
        ],
        screenshot: "https://i.ibb.co/nB9szT1/Screenshot-from-2023-08-14-22-31-16.png"
      },
      {
        "id": "rank",
        "title": "Global Rank",
        "description": "Shows a website's global rank, based on its popularity among the top 100 million sites. Uses data from Tranco, which combines rankings from sources like Umbrella, Majestic, and Cloudflare Radar.",
        "use": "Useful for understanding a site's relative popularity and scale compared to others, and for observing potential trends in site engagement.",
        "resources": [
          { "title": "Tranco List", "link": "https://tranco-list.eu/" },
          { "title": "Tranco Research Paper", "link": "https://tranco-list.eu/assets/tranco-ndss19.pdf" }
        ],
        screenshot: "https://i.ibb.co/wS3F3fw/globalranking.png"
      },
      {
        "id": "block-lists",
        "title": "Block Status",
        "description": "Checks if a URL is accessible via popular privacy, malware, and parental control DNS services, to identify any block listings.",
        "use": "Helps to assess if a site may be restricted or flagged for security, privacy, or content reasons.",
        "resources": [
          { "title": "ThreatJammer Lists", "link": "https://threatjammer.com/osint-lists" }
        ],
        screenshot: "https://i.ibb.co/Xsxr4qk/blocklists.png"
      },
      {
        "id": "threats",
        "title": "Malware & Phishing Check",
        "description": "Checks if a website is flagged on malware or phishing lists, indicating potential security concerns.",
        "use": "Determining a site's threat status is helpful for assessing its reputation and understanding any risks of interaction.",
        "resources": [
          { "title": "URLHaus", "link": "https://urlhaus-api.abuse.ch/" },
          { "title": "PhishTank", "link": "https://www.phishtank.com/" }
        ],
        screenshot: "https://i.ibb.co/ydVx6Pz/threats.png"
      },
      {
        "id": "tls-cipher-suites",
        "title": "TLS Cipher Suites",
        "description": "Analyzes the cryptographic protocols a server uses for secure connections, including algorithms for key exchange, encryption, and data integrity.",
        "use": "Important for evaluating the security of a site's connection protocols, as outdated or weak algorithms can expose the site to attacks.",
        "resources": [
          { "title": "sslscan2 CLI", "link": "https://github.com/rbsec/sslscan" },
          { "title": "ssl-enum-ciphers (NMAP script)", "link": "https://nmap.org/nsedoc/scripts/ssl-enum-ciphers.html" }
        ],
        screenshot: "https://i.ibb.co/6ydtH5R/Screenshot-from-2023-08-26-12-09-58.png"
      },
      {
        "id": "tls-security-config",
        "title": "TLS Security Configuration",
        "description": "Analyzes a site's TLS setup for security weaknesses and compares it against Mozilla's TLS guidelines, offering suggestions for improved configurations.",
        "use": "Identifying weaknesses in TLS configuration helps site owners enhance security and ensure connections are safe and up to date.",
        "resources": [],
        screenshot: "https://i.ibb.co/FmksZJt/Screenshot-from-2023-08-26-12-12-09.png"
      },
      {
        "id": "tls-client-support",
        "title": "TLS Compatibility",
        "description": "Simulates how different clients (like browsers and operating systems) perform TLS handshakes with the server to check compatibility and security settings.",
        "use": "Helps detect compatibility issues and insecure configurations across various client types.",
        "resources": [
          { "title": "TLS Handshakes (Cloudflare Learning)", "link": "https://www.cloudflare.com/learning/ssl/what-happens-in-a-tls-handshake/" },
          { "title": "SSL Test (SSL Labs)", "link": "https://www.ssllabs.com/ssltest/" }
        ],
        screenshot: "https://i.ibb.co/F7qRZkh/Screenshot-from-2023-08-26-12-11-28.png"
      },
      {
        "id": "screenshot",
        "title": "Page Screenshot",
        "description": "Captures an image of the webpage that the URL resolves to, providing a visual of the site.",
        "use": "Useful for seeing how a website appears without browser or location limitations.",
        "resources": [],
        screenshot: "https://i.ibb.co/d6tVFHz/screenshots.png"
      },
    ];

  export const featureIntro = [
    'When conducting OSINT investigations on a specific website or server, several core areas are essential to examine. The tools and techniques for gathering insights on each area are described below.',
    'Redmorph streamlines the data-gathering process, but your expertise is key to analyzing and interpreting the results effectively.',
  ];
  
  export const about = [
    `Redmorph is a comprehensive tool for uncovering information about any given website or server.
    Its principle is straightforward: input a URL, and Redmorph will collect, compile, and present a wide range of open data to help inform your analysis.`,
  
    `The generated report highlights potential security risks, examines protective measures, and maps out connections within the site's infrastructure.
    This insight can also assist with optimizing server configurations, managing redirects, handling cookies, or fine-tuning DNS settings.`,
  
    `Whether you're a developer, system admin, security researcher, penetration tester, or simply curious about the technology behind a site,
    Redmorph is designed to be a valuable addition to your toolkit.`,
  ];
  
  export const license = `The MIT License (MIT)
  Copyright (c) Alicia Sykes <alicia@omg.com> 
  
  Permission is hereby granted, free of charge, to any person obtaining a copy 
  of this software and associated documentation files (the "Software"), to use, 
  copy, modify, merge, publish, distribute, sublicense, and/or sell copies of 
  the Software, and to permit others to whom the Software is provided to do so, 
  under the following conditions:
  
  This copyright notice and permission notice must be included in all copies 
  or substantial portions of the Software.
  
  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
  INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
  PARTICULAR PURPOSE, OR NON-INFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
  HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES, OR OTHER LIABILITY, WHETHER IN AN ACTION
  OF CONTRACT, TORT, OR OTHERWISE, ARISING FROM, OUT OF, OR IN CONNECTION WITH THE
  SOFTWARE OR USE OF THE SOFTWARE.
  `;
  
  export const supportUs = [
    "Redmorph is entirely free and open-source.",
    "The code is fully accessible, allowing you to deploy, fork, modify, and share it for both private and commercial uses.",
    "Running Web-Check incurs some ongoing costs, so if you find the app valuable, consider <a href='https://github.com/sponsors/Lissy93'>sponsoring on GitHub</a>. Even a small monthly donation goes a long way in supporting the project.",
    "Other ways to support include submitting or reviewing pull requests in the <a href='https://github.com/redmorph/rm-x'>GitHub repository</a>, upvoting us on <a href='https://www.producthunt.com/posts/web-check'>Product Hunt</a>, or sharing with your network.",
    "However, there's no obligation—this app and all related projects will remain 100% free and open-source, with managed instances available for as long as possible :)",
  ];
  
  export const fairUse = [
    'Use this tool responsibly. Ensure you have permission before scanning a host, and do not use it to disrupt services.',
    'Requests may be limited to prevent overuse. If you need higher usage, consider deploying your own instance.',
    'Availability is not guaranteed. For critical needs, please deploy your own instance.',
    'Please be mindful of usage, as heavy traffic can quickly exhaust server resources, impacting availability for others and increasing operational costs.',
  ];

  export default docs;
  
  